/**
 * Copy for the certified-product record page.
 *
 * Everything here that describes the certification is regulated text: the
 * six "what it certifies" rows come from B-01 §6 (6.1–6.6), the
 * seven "what it does not certify" rows from B-01 §7, the nine process steps from §8, and the
 * "what it does not certify" framing from §26.2. Reword them only with the
 * mark proprietor, never for design reasons.
 */
const translations = {
  hu: {
    langCurrent: "HU",
    langOther: "EN",
    langToggle: "EN",
    loading: "Termékadatok betöltése…",
    errorTitle: "A termékadatok most nem érhetők el",
    errorBody:
      "Kérjük, próbálja meg később. Ha a probléma tartósan fennáll, írjon nekünk:",
    contactEmail: "info@ultragreencert.com",
    onThisPage: "EZEN AZ OLDALON",
    navCertification: "Mit tanúsít?",
    navNotCertification: "Mit nem tanúsít?",
    navDescription: "Leírás",
    navIngredients: "Összetevők",
    navProcess: "A tanúsítás folyamata",
    navDocuments: "Dokumentumok",
    footerCompany:
      "UltraGreen Kft. · 1039 Budapest, Batthyány utca 35/B. · Cg. 01-09-376313",

    eyebrowRegistry: "NYILVÁNOS TANÚSÍTVÁNY-BEJEGYZÉS",
    heroManufacturer: "GYÁRTÓ",
    heroUpdated: "FRISSÍTVE",
    heroAuthId: "AZONOSÍTÓ",
    railBrandLabel: "A GYÁRTÓ VÉDJEGYE",

    sealStatusLabel: "A BEJEGYZÉS ÁLLAPOTA",
    certHeading: "UltraGreen tanúsított termék",
    sealValidLabel: "ÉRVÉNYES",
    // Hungarian puts the "until" on the date itself; English puts it in the
    // label. Both variants therefore need their own hook.
    sealValidSuffix: "-ig",

    certWhatTitle: "Mit tanúsít az UltraGreen?",
    certWhatBody:
      "A termék összetételét megvizsgálták egy előre meghatározott, nyilvános követelménylista alapján, és a termék ennek megfelelt. A követelménylista bizonyos anyagokat és gyártási eljárásokat kizár, másokra számszerű határértéket ír elő.",
    // B-01 §6 (6.1–6.6) in consumer language, as §28.3 item 1 requires.
    certWhatRows: [
      {
        claim: "Megfelelést egy nyilvános követelménylistának",
        why:
          "A termék összetételét a Védjegybizottság a termékkategóriájára vonatkozó UltraGreen követelménylista alapján értékelte, és a termék minden követelménynek együttesen megfelelt.",
      },
      {
        claim: "A kizárt anyagok hiányát",
        why:
          "Nem tartalmaz olyan vegyületet vagy vegyületcsoportot, amelyet a követelménylista a termékkategóriájában kizár — kozmetikumoknál például fenoxietanolt, parabéneket, kőolajfrakciókat, szilikonokat, nano méretű fém-oxidokat és egyes kémiai fényvédőket.",
      },
      {
        claim: "A kizárt gyártási eljárások mellőzését",
        why:
          "Összetevői nem a termékkategóriájában tiltott kémiai eljárással készültek — kozmetikumoknál például nem etoxilálással (az 1,4-dioxán szennyezés miatt) és nem nyílt téri égetéssel.",
      },
      {
        claim: "A számszerű küszöbértékek teljesülését",
        why:
          "Kozmetikumoknál a természetes, természetes eredetű vagy organikus indexek valamelyike meghaladja a 95%-ot (ISO 16128 szerint, a víz és a nem korlátozott fényvédők nélkül); tisztítószereknél a termék könnyen lebomlik (OECD 301B), és a csomagolás tömege a hasznos tömeg tizedénél kisebb.",
      },
      {
        claim: "A jogszabályi és gyártási megfelelést",
        why:
          "Az előállítás és a minőségellenőrzés megfelel a hatályos európai uniós jogszabályoknak, kozmetikumoknál a helyes gyártási gyakorlat (ISO 22716) követelményeinek is.",
      },
      {
        claim: "A tanúsított összetétel reprodukálhatóságát",
        why:
          "A forgalomba kerülő termék a tanúsított, pontos összetétel szerint készül; a tanúsító ezt piaci próbavásárlással és vizsgálattal rendszeresen ellenőrzi.",
      },
    ],
    // Rows 2–5 of certWhatRows for one registry category (B-01 §6.1–6.3, §6.6): the page shows
    // these when the category is known, and certWhatRows — true for every category — otherwise.
    certWhatCategoryRows: {
      natural_cosmetic: [
        {
          claim: "A kizárt anyagok hiányát",
          why:
            "Nem tartalmaz olyan vegyületet vagy vegyületcsoportot, amelyet a követelménylista a termékkategóriájában kizár — kozmetikumoknál például fenoxietanolt, parabéneket, kőolajfrakciókat, szilikonokat, nano méretű fém-oxidokat és egyes kémiai fényvédőket.",
        },
        {
          claim: "A kizárt gyártási eljárások mellőzését",
          why:
            "Összetevői nem a termékkategóriájában tiltott kémiai eljárással készültek — kozmetikumoknál például nem etoxilálással (az 1,4-dioxán szennyezés miatt) és nem nyílt téri égetéssel.",
        },
        {
          claim: "A számszerű küszöbértékek teljesülését",
          why:
            "A természetes, természetes eredetű vagy organikus indexek valamelyike meghaladja a 95%-ot (ISO 16128 szerint, a víz és a nem korlátozott fényvédők nélkül).",
        },
        {
          claim: "A jogszabályi és gyártási megfelelést",
          why:
            "Az előállítás és a minőségellenőrzés megfelel a hatályos európai uniós jogszabályoknak és a helyes gyártási gyakorlat (ISO 22716) követelményeinek.",
        },
      ],
      eco_detergent: [
        {
          claim: "A kizárt anyagok hiányát",
          why:
            "Nem tartalmaz olyan vegyületet vagy vegyületcsoportot, amelyet a követelménylista a tisztítószerekben kizár — például formaldehidet felszabadító tartósítószereket, parabéneket, szuperabszorbens és egyéb szintetikus polimereket (pl. karbomert), alumíniumvegyületeket, EDTA-t és sóit, valamint foszfátokat és foszfonátokat.",
        },
        {
          claim: "A kizárt gyártási eljárások mellőzését",
          why:
            "Összetevői nem a tisztítószereknél tiltott eljárással készültek — nem elektrolízissel (például nátrium-hidroxid) és nem nyílt téri égetéssel (például hamu).",
        },
        {
          claim: "A számszerű küszöbértékek teljesülését",
          why:
            "A termék könnyen lebomlik (OECD 301B), és a csomagolás tömege a hasznos tömeg tizedénél kisebb.",
        },
        {
          claim: "A jogszabályi és gyártási megfelelést",
          why:
            "Az előállítás és a minőségellenőrzés megfelel a hatályos európai uniós jogszabályoknak, köztük az 1272/2008/EK (CLP) rendeletnek.",
        },
      ],
    },
    scopeLabel: "TERMÉKKÖRÖK",
    scopeValue:
      "Kozmetikai termékek · tisztítószerek · egyéb ipari termékek · biológiailag lebomló műanyag granulátumok és csomagolások",

    // Three parts, because the middle word is set in italic red ink and a
    // heading cannot carry mixed styling as one string.
    certNotTitleBefore: "Mit ",
    certNotTitleEmphasis: "nem",
    certNotTitleAfter: " tanúsít?",
    certNotIntro:
      "A tanúsítás egy meghatározott követelményrendszernek való megfelelést igazol — nem többet. A tanúsítás NEM igazolja:",
    certNotRows: [
      {
        claim: "A termék általános biztonságát",
        why:
          "Meghatározott összetételi és toxikológiai kritériumrendszernek való megfelelést igazol, nem teljes körű biztonsági értékelést.",
      },
      {
        claim: "A kockázatmentességet",
        why:
          "Nem jelent kockázatmentességet, és nem zárja ki a kockázatok fennállását.",
      },
      {
        claim: "A hatósági kockázatértékelés kiváltását",
        why:
          "Nem helyettesíti a jogszabály előírta toxikológiai kockázatértékelést és vizsgálatokat.",
      },
      {
        claim: "A hosszú távú vagy egyéni hatásokat",
        why:
          "Nem veszi figyelembe az előre nem látható hosszú távú hatásokat, az egyéni érzékenységet és a nem rendeltetésszerű használatot.",
      },
      {
        claim: "A minőségi felsőbbrendűséget",
        why:
          "Megfelelőségi jellegű; nem jelent minőségi rangsort vagy kategóriák közötti összehasonlítást.",
      },
      {
        claim: "A környezeti teljesítményt általában",
        why:
          "Összetételi és lebonthatósági kritériumokat vizsgál, nem teljes életciklus-értékelést vagy szénlábnyomot.",
      },
      {
        claim: "A természetes eredetből fakadó előnyt",
        why:
          "A természetes eredet aránya önmagában nem jelent alacsonyabb toxikológiai kockázatot.",
      },
    ],

    reportHeading: "Kétsége van a bejegyzéssel kapcsolatban?",
    reportButton: "Bejelentés a tanúsítással kapcsolatban",
    reportOrEmail: "vagy írjon nekünk:",

    dateLocale: "hu-HU",
    descTitle: "Leírás",
    ingTitle: "Összetevők",
    ingCount: "INCI",
    ingCopy: "INCI-lista másolása",
    ingCopied: "Másolva",
    ingNote:
      "Az összetevők a gyártó által megadott sorrendben, a tanúsított összetétel szerint. A felsorolás nem minősítés: az UltraGreen nem rangsorolja az egyes összetevőket.",

    processTitle: "A tanúsítás folyamata",
    // The actor was a parenthetical inside each step; split out, it becomes a
    // column of its own so a reader can see at a glance whose move it is.
    processSteps: [
      { step: "Írásbeli kérelem benyújtása", actor: "JELENTKEZŐ" },
      {
        step:
          "A minősítési feltételek teljesítését igazoló dokumentumok megküldése",
        actor: "JELENTKEZŐ",
      },
      {
        step: "Formai és teljességi ellenőrzés, hiánypótlás",
        actor: "VÉDJEGYJOGOSULT",
      },
      {
        step:
          "Érdemi vizsgálat: a termékkategóriának megfelelő követelmények teljesülésének értékelése",
        actor: "VÉDJEGYBIZOTTSÁG",
      },
      {
        step: "Döntés és annak részletes írásbeli indokolása",
        actor: "VÉDJEGYJOGOSULT",
      },
      {
        step: "Licencszerződés megkötése",
        actor: "LICENCADÓ ÉS LICENCVEVŐ",
      },
      {
        step: "A C-04 kommunikációs szabályzat elfogadása és alkalmazása",
        actor: "LICENCVEVŐ",
      },
      { step: "Védjegyhasználat megkezdése", actor: "LICENCVEVŐ" },
      { step: "Rendszeres utóellenőrzés próbavásárlással", actor: "TANÚSÍTÓ" },
    ],
    processNotes: [
      "A minősítési eljárást a védjegyjogosult által megbízott Védjegybizottság végzi a mindenkor hatályos tanúsító védjegy szabályzat alapján.",
      "Az eljárás díjfizetéshez kötött; a mindenkori díjszabás nyilvános.",
      "A tanúsított termékeket a piacon rendszeresen, próbavásárlással is ellenőrizzük.",
    ],

    docsTitle: "Dokumentumok és nyilvános lista",
    docsIntro:
      "A teljes követelményrendszer nyilvános. A tanúsított termékek és összetevők kereshetők.",
    docsRegulation: "Tanúsító védjegy szabályzat",
    docsRegulationKind: "04 · PDF",
    docsManual: "Márka- és tanúsítási kézikönyv",
    docsManualKind: "B-01 · PDF",
    docsPrice: "Díjszabás",
    docsPriceKind: "PDF",
    docsSearch: "Tanúsított termékek és összetevők keresése",
    docsSearchKind: "NYILVÁNOS KERESŐ",
    docsOpen: "Megnyitás",

    dialogTitle: "Bejelentés a tanúsítással kapcsolatban",
    dialogIntro:
      "Ha úgy látja, hogy ez a termék nem felel meg az UltraGreen követelményeinek, írja le, mit tapasztalt. A bejelentést a tanúsító vizsgálja ki.",
    dialogMessageLabel: "MIT TAPASZTALT?",
    dialogMessagePlaceholder: "Legalább 10 karakter…",
    dialogEmailLabel: "E-MAIL CÍM (NEM KÖTELEZŐ)",
    dialogEmailHelp: "Csak akkor adja meg, ha visszajelzést szeretne kapni.",
    dialogSubmit: "Bejelentés elküldése",
    dialogCancel: "Mégsem",
    dialogSending: "Küldés…",
    dialogSentTitle: "Köszönjük, a bejelentést rögzítettük",
    dialogSentBody:
      "A tanúsítási bejelentéseket megvizsgáljuk, és szükség esetén eljárunk a licencvevővel szemben.",
    dialogClose: "Bezárás",
    dialogErrorGeneric:
      "A bejelentést most nem sikerült elküldeni. Kérjük, próbálja újra, vagy írjon a következő címre:",
    dialogErrorRateLimit:
      "Túl sok bejelentés érkezett erről a hálózatról. Kérjük, próbálja meg később, vagy írjon a következő címre:",
  },

  en: {
    langCurrent: "EN",
    langOther: "HU",
    langToggle: "HU",
    loading: "Loading product data…",
    errorTitle: "Product data is unavailable right now",
    errorBody:
      "Please try again later. If the problem persists, write to us at:",
    contactEmail: "info@ultragreencert.com",
    onThisPage: "ON THIS PAGE",
    navCertification: "What it certifies",
    navNotCertification: "What it does not certify",
    navDescription: "Description",
    navIngredients: "Ingredients",
    navProcess: "The certification process",
    navDocuments: "Documents",
    footerCompany:
      "UltraGreen Kft. · 1039 Budapest, Batthyány utca 35/B. · Reg. 01-09-376313",

    eyebrowRegistry: "PUBLIC CERTIFICATION RECORD",
    heroManufacturer: "MANUFACTURER",
    heroUpdated: "UPDATED",
    heroAuthId: "RECORD ID",
    railBrandLabel: "THE MANUFACTURER'S MARK",

    sealStatusLabel: "RECORD STATUS",
    certHeading: "UltraGreen certified product",
    sealValidLabel: "VALID UNTIL",
    sealValidSuffix: "",

    certWhatTitle: "What does UltraGreen certify?",
    certWhatBody:
      "The product's composition was assessed against a predefined, public list of criteria, and the product met it. The list excludes certain substances and manufacturing processes and sets numeric thresholds for others.",
    certWhatRows: [
      {
        claim: "Compliance with a public list of criteria",
        why:
          "The Certification Committee assessed the product's composition against the UltraGreen criteria for its product category, and the product met all of them together.",
      },
      {
        claim: "The absence of excluded substances",
        why:
          "It contains no substance or substance group that the criteria exclude for its category — for cosmetics, for example, phenoxyethanol, parabens, petroleum fractions, silicones, nano-sized metal oxides and certain chemical UV filters.",
      },
      {
        claim: "No excluded manufacturing processes",
        why:
          "Its ingredients were not made by a chemical process prohibited for its category — for cosmetics, for example, not by ethoxylation (because of 1,4-dioxane contamination) and not by open-air burning.",
      },
      {
        claim: "Numeric thresholds met",
        why:
          "For cosmetics, at least one of the natural, natural-origin or organic indexes exceeds 95% (per ISO 16128, excluding water and unrestricted UV filters); for detergents, the product is readily biodegradable (OECD 301B) and its packaging weighs less than a tenth of its usable mass.",
      },
      {
        claim: "Legal and manufacturing compliance",
        why:
          "Production and quality control comply with applicable EU law and, for cosmetics, with good manufacturing practice (ISO 22716).",
      },
      {
        claim: "A reproducible certified composition",
        why:
          "The product on the market is made to the exact certified composition; the certifier checks this regularly through market test purchases and analysis.",
      },
    ],
    // Rows 2–5 of certWhatRows for one registry category (B-01 §6.1–6.3, §6.6): the page shows
    // these when the category is known, and certWhatRows — true for every category — otherwise.
    certWhatCategoryRows: {
      natural_cosmetic: [
        {
          claim: "The absence of excluded substances",
          why:
            "It contains no substance or substance group that the criteria exclude for its category — for cosmetics, for example, phenoxyethanol, parabens, petroleum fractions, silicones, nano-sized metal oxides and certain chemical UV filters.",
        },
        {
          claim: "No excluded manufacturing processes",
          why:
            "Its ingredients were not made by a chemical process prohibited for its category — for cosmetics, for example, not by ethoxylation (because of 1,4-dioxane contamination) and not by open-air burning.",
        },
        {
          claim: "Numeric thresholds met",
          why:
            "At least one of the natural, natural-origin or organic indexes exceeds 95% (per ISO 16128, excluding water and unrestricted UV filters).",
        },
        {
          claim: "Legal and manufacturing compliance",
          why:
            "Production and quality control comply with applicable EU law and with good manufacturing practice (ISO 22716).",
        },
      ],
      eco_detergent: [
        {
          claim: "The absence of excluded substances",
          why:
            "It contains no substance or substance group that the criteria exclude for detergents — for example, formaldehyde-releasing preservatives, parabens, superabsorbent and other synthetic polymers (e.g. carbomer), aluminium compounds, EDTA and its salts, and phosphates and phosphonates.",
        },
        {
          claim: "No excluded manufacturing processes",
          why:
            "Its ingredients were not made by a process prohibited for detergents — not by electrolysis (e.g. sodium hydroxide) and not by open-air burning (e.g. ash).",
        },
        {
          claim: "Numeric thresholds met",
          why:
            "The product is readily biodegradable (OECD 301B), and its packaging weighs less than a tenth of its usable mass.",
        },
        {
          claim: "Legal and manufacturing compliance",
          why:
            "Production and quality control comply with applicable EU law, including the CLP Regulation (EC) No 1272/2008.",
        },
      ],
    },
    scopeLabel: "SCOPE",
    scopeValue:
      "Cosmetics · detergents · other industrial products · biodegradable plastic granules and packaging",

    certNotTitleBefore: "What it does ",
    certNotTitleEmphasis: "not",
    certNotTitleAfter: " certify",
    certNotIntro:
      "The certification confirms compliance with a defined set of criteria — nothing more. It does NOT confirm:",
    certNotRows: [
      {
        claim: "The general safety of the product",
        why:
          "It confirms compliance with a defined compositional and toxicological criteria set, not a full safety evaluation.",
      },
      {
        claim: "Freedom from risk",
        why:
          "It does not mean the product is risk-free, nor does it exclude risks.",
      },
      {
        claim: "A substitute for regulatory risk assessment",
        why:
          "It does not replace the toxicological risk assessment and testing required by law.",
      },
      {
        claim: "Long-term or individual effects",
        why:
          "It does not account for unforeseeable long-term effects, individual sensitivity, or use other than as intended.",
      },
      {
        claim: "Superior quality",
        why:
          "It is a conformity statement; it implies no quality ranking and no comparison between categories.",
      },
      {
        claim: "Environmental performance in general",
        why:
          "It examines compositional and biodegradability criteria, not a full life-cycle assessment or carbon footprint.",
      },
      {
        claim: "A benefit arising from natural origin",
        why:
          "The proportion of natural origin does not by itself mean a lower toxicological risk.",
      },
    ],

    reportHeading: "Have a concern about this record?",
    reportButton: "Report a certification concern",
    reportOrEmail: "or write to us:",

    dateLocale: "en-GB",
    descTitle: "Description",
    ingTitle: "Ingredients",
    ingCount: "INCI",
    ingCopy: "Copy INCI list",
    ingCopied: "Copied",
    ingNote:
      "Ingredients in the order given by the manufacturer, as certified. The list is not an assessment: UltraGreen does not rank individual ingredients.",

    processTitle: "The certification process",
    processSteps: [
      { step: "Written application submitted", actor: "APPLICANT" },
      {
        step: "Documents evidencing compliance with the criteria submitted",
        actor: "APPLICANT",
      },
      {
        step: "Formal and completeness check, request for missing items",
        actor: "MARK PROPRIETOR",
      },
      {
        step:
          "Substantive assessment against the criteria for the product category",
        actor: "CERTIFICATION COMMITTEE",
      },
      {
        step: "Decision with detailed written reasons",
        actor: "MARK PROPRIETOR",
      },
      { step: "Licence agreement concluded", actor: "LICENSOR AND LICENSEE" },
      {
        step: "Acceptance and application of the C-04 communication policy",
        actor: "LICENSEE",
      },
      { step: "Use of the mark begins", actor: "LICENSEE" },
      { step: "Regular re-checking by test purchase", actor: "CERTIFIER" },
    ],
    processNotes: [
      "The assessment is carried out by the Certification Committee appointed by the mark proprietor, under the certification mark policy in force.",
      "The procedure is subject to a fee; the fee schedule is public.",
      "Certified products are re-checked on the market, including by test purchase.",
    ],

    docsTitle: "Documents and public list",
    docsIntro:
      "The full set of criteria is public. Certified products and ingredients are searchable.",
    docsRegulation: "Certification mark policy",
    docsRegulationKind: "04 · PDF",
    docsManual: "Brand & Certification Manual",
    docsManualKind: "B-01 · PDF",
    docsPrice: "Fee schedule",
    docsPriceKind: "PDF",
    docsSearch: "Search certified products and ingredients",
    docsSearchKind: "PUBLIC SEARCH",
    docsOpen: "Open",

    dialogTitle: "Report a certification concern",
    dialogIntro:
      "If you believe this product does not meet the UltraGreen criteria, describe what you found. The certifier investigates every report.",
    dialogMessageLabel: "WHAT DID YOU FIND?",
    dialogMessagePlaceholder: "At least 10 characters…",
    dialogEmailLabel: "EMAIL ADDRESS (OPTIONAL)",
    dialogEmailHelp: "Only needed if you would like a reply.",
    dialogSubmit: "Send report",
    dialogCancel: "Cancel",
    dialogSending: "Sending…",
    dialogSentTitle: "Thank you — your report has been recorded",
    dialogSentBody:
      "We review certification concerns and act towards the licensee where needed.",
    dialogClose: "Close",
    dialogErrorGeneric:
      "The report could not be sent right now. Please try again, or write to:",
    dialogErrorRateLimit:
      "Too many reports from this network. Please try again later, or write to:",
  },
};

export default translations;
